/**
 * Activity Log Service
 *
 * FERPA-compliant activity logging service for tracking coach→athlete data access.
 *
 * Usage:
 * ```typescript
 * import { activityLogService } from './services/compliance/activityLogService';
 *
 * // Log an activity (fire-and-forget, non-blocking)
 * activityLogService.log({
 *   action_type: 'VIEW_ROWER_JOURNEY',
 *   resource_type: 'athlete',
 *   resource_id: rowerProfileId,
 *   metadata: {
 *     athlete_name: 'Jane Smith',
 *     team_id: activeTeam.id
 *   }
 * });
 * ```
 *
 * Features:
 * - Fire-and-forget logging (doesn't block UI)
 * - Automatic server-side context capture (IP, timestamp, user_role)
 * - Type-safe metadata with flexible JSONB storage
 * - Silent error handling (logs to console, doesn't throw)
 */

import { supabase } from '../../config/supabase';
import { ActivityLogWithProfile } from './activityLogCsvExport';

// ============================================================================
// Types
// ============================================================================

export interface LogActivityParams {
  action_type: string;
  resource_type?: string | null;
  resource_id?: string | null;
  metadata?: Record<string, any> | null;
}

export interface LogActivityResult {
  success: boolean;
  log_id?: string;
  error?: string;
}

export interface ActivityLogFilters {
  user_id?: string;
  action_type?: string;
  resource_type?: string;
  start_date?: string;
  end_date?: string;
  search?: string;
  offset?: number;
  limit?: number;
}

export interface ActivityLogsResult {
  success: boolean;
  data: ActivityLogWithProfile[] | null;
  totalCount?: number;
  error?: string;
}

// ============================================================================
// Activity Log Service Class
// ============================================================================

class ActivityLogService {
  /**
   * Log a FERPA-compliant activity
   *
   * Fire-and-forget pattern - does not block UI rendering.
   * Server-side RPC function captures: user_id, user_role, ip_address, timestamp.
   *
   * @param params - Activity logging parameters
   * @returns Promise with success status and log ID
   *
   * @example
   * await activityLogService.log({
   *   action_type: 'VIEW_ROWER_JOURNEY',
   *   resource_type: 'athlete',
   *   resource_id: 'uuid-123',
   *   metadata: { team_id: 'uuid-456', display_code: 'ABC12XY' }
   * });
   */
  async log(params: LogActivityParams): Promise<LogActivityResult> {
    try {
      // Enrich metadata with actor name so athletes can see who accessed their data
      // without needing an RLS-crossing join on the profiles table
      let enrichedMetadata = params.metadata || {};
      if (!enrichedMetadata.actor_name) {
        const { data: profile } = await supabase
          .from('profiles')
          .select('full_name')
          .eq('id', (await supabase.auth.getUser()).data.user?.id ?? '')
          .single();
        if (profile?.full_name) {
          enrichedMetadata = { ...enrichedMetadata, actor_name: profile.full_name };
        }
      }

      const { data, error } = await supabase.rpc('record_activity_log', {
        p_action_type: params.action_type,
        p_resource_type: params.resource_type || null,
        p_resource_id: params.resource_id || null,
        p_metadata: enrichedMetadata,
      });

      if (error) {
        console.error('[FERPA] Activity log failed:', error);
        return { success: false, error: error.message };
      }

      return { success: true, log_id: data as string };
    } catch (error) {
      console.error('[FERPA] Activity log exception:', error);
      return { success: false, error: String(error) };
    }
  }

  /**
   * Get activity logs for the current user
   *
   * Used by athletes to view "who accessed my data" history.
   * RLS policies ensure users only see logs where they are the subject.
   *
   * @param limit - Maximum number of logs to return (default: 100)
   * @returns Promise with activity logs or error
   *
   * @example
   * const result = await activityLogService.getMyActivityLogs(50);
   * if (result.success) {
   *   console.log('My access history:', result.data);
   * }
   */
  async getMyActivityLogs(limit: number = 100) {
    try {
      const { data, error } = await supabase
        .from('activity_log')
        .select(
          `
          *,
          profiles:user_id (
            full_name,
            email
          )
        `
        )
        .order('created_at', { ascending: false })
        .limit(limit);

      if (error) throw error;
      return { success: true, data };
    } catch (error) {
      console.error('[FERPA] Failed to fetch activity logs:', error);
      return { success: false, error: String(error), data: null };
    }
  }

  /**
   * Get all activity logs with optional filters
   *
   * Used by compliance officers for audit purposes.
   * RLS policies enforce admin-only access to all logs.
   *
   * @param filters - Optional filters (user_id, action_type, date range, etc.)
   * @returns Promise with filtered activity logs or error
   *
   * @example
   * const result = await activityLogService.getAllActivityLogs({
   *   action_type: 'VIEW_ROWER_JOURNEY',
   *   start_date: '2025-12-01',
   *   end_date: '2025-12-31',
   *   limit: 500
   * });
   */
  async getAllActivityLogs(filters?: ActivityLogFilters): Promise<ActivityLogsResult> {
    try {
      const limit = filters?.limit ?? 500;
      const offset = filters?.offset ?? 0;
      const search = filters?.search?.trim();

      let query = supabase
        .from('activity_log')
        .select(
          `
          *,
          profiles:user_id (
            full_name,
            email
          )
        `,
          { count: 'exact' }
        )
        .order('created_at', { ascending: false });

      if (filters?.user_id) {
        query = query.eq('user_id', filters.user_id);
      }
      if (filters?.action_type) {
        query = query.eq('action_type', filters.action_type);
      }
      if (filters?.resource_type) {
        query = query.eq('resource_type', filters.resource_type);
      }
      if (filters?.start_date) {
        query = query.gte('created_at', filters.start_date);
      }
      if (filters?.end_date) {
        query = query.lte('created_at', filters.end_date);
      }
      if (search) {
        const term = `%${search.replace(/[%_\\]/g, '\\$&')}%`;
        query = query.or(
          `action_type.ilike.${term},resource_type.ilike.${term},resource_id.ilike.${term},ip_address.ilike.${term}`
        );
      }

      query = query.range(offset, offset + limit - 1);

      const { data, error, count } = await query;

      if (error) throw error;
      return {
        success: true,
        data: (data ?? []) as unknown as ActivityLogWithProfile[],
        totalCount: count ?? 0,
      };
    } catch (error) {
      console.error('[FERPA] Failed to fetch all activity logs:', error);
      return { success: false, error: String(error), data: null };
    }
  }
}

// ============================================================================
// Export singleton instance
// ============================================================================

/**
 * Singleton instance of ActivityLogService
 *
 * Import this in your components to log activities:
 * ```typescript
 * import { activityLogService } from './services/compliance/activityLogService';
 * ```
 */
export const activityLogService = new ActivityLogService();
